import { DEMO_CONNECTION_INDEX } from 'portal-sdk';

import { Project } from 'src/store/state/projects';

type ContextConnection = { id: string; index: number };

type ProjectContext = {
  defaultConnection: ContextConnection | null;
  portalConnections: ContextConnection[] | null;
  currentOrgDomain: string | undefined;
};

/**
 * Whether a project should appear on the home page for the current connection
 * and org context.
 *
 * The same Stardog server has a different connection id in a personal vs. org
 * context, so a project's connection only resolves in the context it was
 * created under. `orgDomain` is undefined for personal projects; normalize
 * both sides so a personal project doesn't match an org context (and vice
 * versa) when falling back to the "connection no longer exists" rule — without
 * this, org and personal projects leak across contexts via that fallback.
 */
export const isProjectVisibleInContext = (
  project: Pick<Project, 'connectionId' | 'orgDomain'>,
  { defaultConnection, portalConnections, currentOrgDomain }: ProjectContext
): boolean => {
  const belongsToCurrentContext =
    (project.orgDomain || undefined) === (currentOrgDomain || undefined);

  return (
    // show all projects on the demo connection
    defaultConnection?.index === DEMO_CONNECTION_INDEX ||
    // project has no connection assigned
    !project.connectionId ||
    // project is for the current connection
    project.connectionId === defaultConnection?.id ||
    // project connection no longer exists in this context — only surface it
    // when the project belongs here, else org/personal projects leak across
    // contexts via this fallback
    Boolean(
      belongsToCurrentContext &&
        portalConnections &&
        !portalConnections.some((c) => c.id === project.connectionId)
    )
  );
};
